import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Alert,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import HtmlEditor from "./question_form/HtmlEditor";
import ImagePickerDialog from "./ImagePickerDialog";

import {
    createAuthor,
    setAuthorEnabled,
    getAuthorsAdminBySubject,
    patchAuthor,
} from "../../api/subjects";

const AuthorsAdminPanel = ({ classId, subjectSlug }) => {
    const nav = useNavigate();

    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [name, setName] = useState("");
    const [authorSlug, setAuthorSlug] = useState("");
    const [order, setOrder] = useState(0);
    const [infoHtml, setInfoHtml] = useState("");
    const [creating, setCreating] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAuthor, setEditingAuthor] = useState(null);
    const [editInfoHtml, setEditInfoHtml] = useState("");
    const [editTextbookTitle, setEditTextbookTitle] = useState("");
    const [editCoverImagePath, setEditCoverImagePath] = useState("");
    const [coverPickerOpen, setCoverPickerOpen] = useState(false);
    const [savingInfo, setSavingInfo] = useState(false);

    const [snack, setSnack] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const loadAuthors = useCallback(
        async (opts) => {
            const silent = Boolean(opts && opts.silent);
            if (!classId || !subjectSlug) return;

            if (!silent) setLoading(true);

            try {
                const data = await getAuthorsAdminBySubject(classId, subjectSlug);
                setAuthors(Array.isArray(data) ? data : []);
            } catch (e) {
                const msg =
                    e?.response?.data ||
                    e?.message ||
                    "Не вдалося завантажити список авторів";
                setSnack({ open: true, severity: "error", message: String(msg) });
                setAuthors([]);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [classId, subjectSlug]
    );

    useEffect(() => {
        loadAuthors();
    }, [loadAuthors]);

    function openDialog() {
        setName("");
        setAuthorSlug("");
        setOrder(0);
        setInfoHtml("");
        setDialogOpen(true);
    }

    function closeDialog() {
        if (!creating) setDialogOpen(false);
    }

    async function onCreate() {
        const n = String(name || "").trim();
        const s = String(authorSlug || "").trim();

        if (!n) {
            setSnack({ open: true, severity: "error", message: "Введіть ім'я автора" });
            return;
        }
        if (!s) {
            setSnack({ open: true, severity: "error", message: "Введіть коротку назву автора" });
            return;
        }

        setCreating(true);
        try {
            await createAuthor(classId, subjectSlug, {
                name: n,
                authorSlug: s,
                order: Number(order) || 0,
                infoHtml,
            });

            setSnack({ open: true, severity: "success", message: "Автора успішно додано" });
            setDialogOpen(false);
            await loadAuthors({ silent: true });
        } catch (e) {
            const status = e?.response?.status;
            const msg = e?.response?.data || e?.message || "Не вдалося додати автора";
            setSnack({
                open: true,
                severity: "error",
                message: status ? `${status}: ${msg}` : String(msg),
            });
        } finally {
            setCreating(false);
        }
    }

    function openEditDialog(author) {
        setEditingAuthor(author);
        setEditInfoHtml(author?.infoHtml || "");
        setEditTextbookTitle(author?.textbookTitle || "");
        setEditCoverImagePath(author?.coverImagePath || "");
        setEditDialogOpen(true);
    }

    async function onSaveInfo() {
        if (!editingAuthor?.authorSlug) return;
        setSavingInfo(true);
        try {
            const saved = await patchAuthor(classId, subjectSlug, editingAuthor.authorSlug, {
                infoHtml: editInfoHtml,
                textbookTitle: editTextbookTitle,
                coverImagePath: editCoverImagePath,
            });
            setAuthors((prev) => prev.map((author) => (
                author.authorSlug === editingAuthor.authorSlug ? { ...author, ...saved } : author
            )));
            setEditDialogOpen(false);
            setSnack({ open: true, severity: "success", message: "Інформацію про е-додаток збережено" });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося зберегти інформацію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setSavingInfo(false);
        }
    }

    async function onToggleEnabled(slug, next) {
        try {
            await setAuthorEnabled(classId, subjectSlug, slug, next);

            setAuthors((prev) =>
                prev.map((x) => (x.authorSlug === slug ? { ...x, enabled: next } : x))
            );

            setSnack({
                open: true,
                severity: "success",
                message: next ? "Автора увімкнено" : "Автора вимкнено",
            });
        } catch (err) {
            const msg =
                err?.response?.data || err?.message || "Не вдалося змінити статус";
            setSnack({ open: true, severity: "error", message: String(msg) });
        }
    }

    return (
        <Container disableGutters maxWidth={false} sx={{ mt: 2 }}>
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Автори
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Керування авторами
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openDialog}
                        >
                            Додати
                        </Button>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Імʼя</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Коротка назва
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                            Порядок
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 140 }}>
                                            Активний
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 250 }}>
                                            Дії
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Завантаження...
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ) : authors.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ color: "text.secondary" }}>
                                                Список порожній
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        authors.map((a) => (
                                            <TableRow
                                                key={a.id || `${a.classId}-${a.subjectSlug}-${a.authorSlug}`}
                                                sx={{
                                                    ...(a.enabled ? {} : { opacity: 0.5 }),
                                                }}
                                            >
                                                <TableCell
                                                    sx={
                                                        !a.enabled
                                                            ? { textDecoration: "line-through" }
                                                            : undefined
                                                    }
                                                >
                                                    {a.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{ fontFamily: "monospace" }}
                                                    >
                                                        {a.authorSlug}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{a.order ?? 0}</TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={Boolean(a.enabled)}
                                                        onChange={(e) =>
                                                            onToggleEnabled(a.authorSlug, e.target.checked)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(a)}>
                                                            Інформація
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() =>
                                                                nav(
                                                                    `/manage-portal-9f3a/classes/${classId}/subjects/${subjectSlug}/authors/${a.authorSlug}/modules`
                                                                )
                                                            }
                                                        >
                                                            Модулі
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Додати автора</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Імʼя"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Наприклад: Прошкіна"
                            fullWidth
                        />

                        <TextField
                            label="Коротка назва для URL"
                            value={authorSlug}
                            onChange={(e) => setAuthorSlug(e.target.value)}
                            placeholder="Наприклад: prosh"
                            fullWidth
                        />

                        <TextField
                            label="Порядок"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            fullWidth
                        />

                        <HtmlEditor
                            label="Інформація про е-додаток"
                            value={infoHtml}
                            onChange={setInfoHtml}
                            helperText="Необов'язково. Назва, автори, гриф та інша офіційна інформація."
                            minHeight={160}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={creating}>
                        Скасувати
                    </Button>
                    <Button variant="contained" onClick={onCreate} disabled={creating}>
                        {creating ? <CircularProgress size={18} /> : "Додати"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={editDialogOpen}
                onClose={() => !savingInfo && setEditDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Інформація про е-додаток</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                        {editingAuthor?.name}
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Назва підручника в каталозі"
                            value={editTextbookTitle}
                            onChange={(e) => setEditTextbookTitle(e.target.value)}
                            placeholder="Якщо не заповнювати, використовується назва предмета і клас"
                            fullWidth
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                            <TextField label="Обкладинка" value={editCoverImagePath} onChange={(e) => setEditCoverImagePath(e.target.value)} fullWidth />
                            <Button variant="outlined" onClick={() => setCoverPickerOpen(true)} sx={{ minWidth: 170 }}>Обрати зображення</Button>
                            {editCoverImagePath && <Button color="error" onClick={() => setEditCoverImagePath("")}>Прибрати</Button>}
                        </Stack>
                        {editCoverImagePath && <Box component="img" src={editCoverImagePath} alt="Попередній перегляд обкладинки" sx={{ width: 120, maxHeight: 170, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }} />}
                        <HtmlEditor
                            label="Офіційна та додаткова інформація"
                            value={editInfoHtml}
                            onChange={setEditInfoHtml}
                            helperText="Можна вставляти форматований текст із Word."
                            minHeight={240}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={savingInfo}>Скасувати</Button>
                    <Button variant="contained" onClick={onSaveInfo} disabled={savingInfo}>
                        {savingInfo ? <CircularProgress size={18} /> : "Зберегти"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ImagePickerDialog
                open={coverPickerOpen}
                onClose={() => setCoverPickerOpen(false)}
                onPick={(image) => {
                    setEditCoverImagePath(image.url);
                    setCoverPickerOpen(false);
                }}
            />

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AuthorsAdminPanel;
